// LiteAPI hotel-type ids → human labels.
// Source of truth: LiteAPI /data/hotelTypes endpoint.
export const HOTEL_TYPE_NAMES = {
  0: "Not available",
  201: "Apartments",
  203: "Hostels",
  204: "Hotels",
  205: "Motels",
  206: "Resorts",
  207: "Residences",
  208: "Bed & breakfasts",
  209: "Ryokans",
  210: "Farm stays",
  212: "Holiday parks",
  213: "Villas",
  214: "Campsites",
  215: "Boats",
  216: "Guest houses",
  218: "Inns",
  219: "Aparthotels",
  220: "Holiday homes",
  221: "Lodges",
  222: "Homestays",
  223: "Country houses",
  224: "Luxury tents",
  225: "Capsule hotels",
  226: "Love hotels",
  227: "Riads",
  228: "Chalets",
  229: "Condos",
  230: "Cottages",
  231: "Economy hotels",
  232: "Gites",
  233: "Health resorts",
  234: "Cruises",
  235: "Student accommodation",
  243: "Tree house",
  247: "Pensions",
  250: "Private vacation home",
  251: "Pousadas",
  252: "Country house",
  254: "Campsite",
  257: "Cabin",
  258: "Holiday park",
  262: "Affittacamere",
  264: "Hostel / backpacker",
  265: "Houseboat",
  268: "Ranch",
  271: "Agritourism",
  272: "Mobile home",
  273: "Safari tent",
  274: "All-inclusive",
  276: "Castle",
  277: "Property",
  278: "Palace"
};

// Grouping for the results-page tabs.
// Hotels bucket = traditional keyed / front-desk lodging.
// Vacation rentals bucket = self-catering: whole homes, condos, villas.
export const HOTEL_BUCKET = new Set([
  204, // Hotels
  206, // Resorts
  208, // Bed & breakfasts
  216, // Guest houses
  218, // Inns
  205, // Motels
  231, // Economy hotels
  221, // Lodges
  225, // Capsule hotels
  233  // Health resorts
]);

export const RENTAL_BUCKET = new Set([
  201, // Apartments
  213, // Villas
  219, // Aparthotels (treated as rentals — self-catering suites)
  220, // Holiday homes
  229, // Condos
  250, // Private vacation home
  230, // Cottages
  252, // Country house
  228, // Chalets
  257, // Cabin
  258, // Holiday park
  272, // Mobile home
  207, // Residences
  212, // Holiday parks
  222, // Homestays
  223, // Country houses
  224, // Luxury tents
  243, // Tree house
  265, // Houseboat
  268  // Ranch
]);

export function typeLabel(hotelTypeId) {
  return HOTEL_TYPE_NAMES[hotelTypeId] || "Property";
}

// ─── Lodging classification for structured data ─────────────────────────────
// Hotel Center validates against schema.org/Hotel, so every lodging entity is
// typed Hotel. Self-catering places are additionally typed VacationRental so
// Google (and Search) sees them as vacation rentals, never as hotels.
export function lodgingKind(hotelTypeId) {
  return RENTAL_BUCKET.has(Number(hotelTypeId)) ? "rental" : "hotel";
}

export function lodgingLabel(hotelTypeId) {
  return lodgingKind(hotelTypeId) === "rental" ? "Vacation rental" : "Hotel";
}

// schema.org @type for a JSON-LD lodging node. VacationRental is only added
// when the node can actually satisfy Google's VacationRental requirements
// (containsPlace with occupancy) — see lodgingNode() in seo.js, which owns
// that decision. An incomplete VacationRental is discarded by Google outright,
// so a valid Hotel beats an invalid VacationRental.
export function lodgingSchemaTypes(hotelTypeId, { vacationRental = false } = {}) {
  return (vacationRental && lodgingKind(hotelTypeId) === "rental") ? ["Hotel", "VacationRental"] : "Hotel";
}

// Space-separated itemtype list for Microdata scopes. Always Hotel +
// LodgingReservation, for rentals too: this scope exists only for Hotel
// Center's price crawler, which the crawler guide says to co-type exactly
// that way. Adding VacationRental here would make Search validate a
// price-only scope against the vacation-rental spec and fail it.
export function lodgingMicrodataTypes() {
  return "https://schema.org/Hotel https://schema.org/LodgingReservation";
}

// additionalType: for rentals Google's vacation-rental vocabulary
// (Apartment, Cabin, Chalet, Cottage, Gite, HolidayVillageRental, House, Villa);
// for hotels the matching schema.org LodgingBusiness subtype.
const RENTAL_ADDITIONAL = {
  201: "Apartment", 207: "Apartment", 219: "Apartment", 229: "Apartment",
  213: "Villa",
  220: "House", 250: "House", 252: "House", 223: "House", 268: "House", 222: "House",
  257: "Cabin", 228: "Chalet", 230: "Cottage", 232: "Gite",
  212: "HolidayVillageRental", 258: "HolidayVillageRental"
};
const HOTEL_ADDITIONAL = {
  206: "https://schema.org/Resort", 208: "https://schema.org/BedAndBreakfast",
  203: "https://schema.org/Hostel", 264: "https://schema.org/Hostel",
  205: "https://schema.org/Motel",
  214: "https://schema.org/Campground", 254: "https://schema.org/Campground"
};
export function lodgingAdditionalType(hotelTypeId) {
  const id = Number(hotelTypeId);
  return lodgingKind(id) === "rental" ? (RENTAL_ADDITIONAL[id] || "VacationRental") : (HOTEL_ADDITIONAL[id] || undefined);
}
